import { navStore } from "../camera/nav.js";
import { vehicleAudio } from "./audio/audio.js";
import { vehicleHub } from "./hub.js";
import { vehicleStore } from "./store.js";

/**
 * Mock-mode test hook (`__uzVehicles`): reads the vehicle state and drives it without pixel
 * picking. Deterministic: tests set the keys and read the pose, nothing depends on timers.
 */
export function installVehicleHook(): () => void {
  if (import.meta.env["VITE_UZ_MOCK"] !== "1") return () => undefined;
  const hook = {
    state: () => {
      const s = vehicleStore.getState();
      return {
        catalog: s.catalog.length,
        mine: s.mine.map((v) => ({ ...v })),
        credits: s.credits,
        drivingId: s.drivingId,
        galeriOpen: s.galeri.open,
        topUpOpen: s.topUp.open,
        near: s.near,
        error: s.error,
        navMode: navStore.getState().mode,
      };
    },
    car: () => {
      const c = vehicleStore.car;
      return c
        ? { x: c.x, z: c.z, yaw: c.yaw, kmh: Math.hypot(c.vx, c.vz) * 3.6, distance: c.distance }
        : null;
    },
    /** The map camera over a spot (screenshots). */
    look: (x: number, z: number, dx = 26, h = 16, dz = 30) => {
      if (navStore.getState().mode !== "orbit") navStore.setMode("orbit");
      navStore.requestFlight([x, 1, z], [x + dx, h, z + dz]);
    },
    openGaleri: (dealerId: string | null = null) => vehicleStore.openGaleri(dealerId),
    buy: (modelId: string, color: string) => vehicleStore.buy(modelId, color),
    enter: (id: string) => vehicleStore.enter(id),
    exit: () => vehicleStore.requestExit(),
    /** The driving camera: chase, cockpit or hood. */
    camera: (mode: "chase" | "cockpit" | "hood") => {
      vehicleStore.camMode = mode;
    },
    /** The boarding sequence and the door (0 shut .. 1 open) of the driven car. */
    board: () => ({ phase: vehicleStore.boardPhase, door: vehicleStore.visual.door }),
    /** Plays another driver: a pose message as the server would relay it. */
    remotePose: (
      vehicleId: string,
      x: number,
      z: number,
      yawDeg: number,
      speed = 0,
      steer?: number,
      lights?: number,
    ) => vehicleHub.ingest({ type: "vehicle.pose", vehicleId, x, z, yawDeg, speed, steer, lights }),
    /** Another driver's car as drawn: wheel steer and lamps (from the stream or derived). */
    remoteLook: (vehicleId: string) => {
      const p = vehicleStore.poses.get(vehicleId);
      return p ? { steer: p.steer ?? null, lights: p.lights ?? null } : null;
    },
    sentPoses: () => [...vehicleHub.sent],
    /** What the vehicle audio is doing (context state, engine voice, 3D voices, settings). */
    audio: () => vehicleAudio.status(),
    teleportCar: (x: number, z: number, yaw: number) => {
      vehicleStore.teleport = { x, z, yaw };
    },
    /** The physics of the driven car: gear, rpm, wheel spin and contact. */
    drive: () => {
      const s = vehicleStore.sim;
      return s
        ? {
            gear: s.box.gear,
            rpm: s.box.rpm,
            speed: s.speed,
            wheels: s.wheels.map((w) => ({ spin: w.spin, contact: w.contact, slide: w.slide })),
            sleeping: s.body.isSleeping(),
          }
        : null;
    },
  };
  (globalThis as { __uzVehicles?: typeof hook }).__uzVehicles = hook;
  return () => {
    delete (globalThis as { __uzVehicles?: typeof hook }).__uzVehicles;
  };
}
